import { useParams } from 'react-router-dom';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { toast } from 'react-toastify';
import { StatusCodes } from 'http-status-codes';
import { getErrorMessage, getErrorStatus } from '@common/utils';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '../ui/button';
import { useMutatePublishProject } from '@/reactQuery/post';
import useTranslation from '@/hooks/useTranslation';
import { ECompanyThemeName, EPublishErrorMessage } from '@/enums';

interface PublishProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectTitle?: string;
  projectId?: string;
  isPublishRequestApproved: boolean;
}

function PublishProjectModal({
  isOpen,
  onClose,
  projectTitle,
  projectId,
  isPublishRequestApproved,
}: PublishProjectModalProps) {
  const { organization_id: organizationId, workspace_id: workspaceId } = useParams();
  const { lang } = useTranslation();
  const { themeName } = useTheme();

  const { mutateAsync: mutateAsyncPublishProject, isPending } = useMutatePublishProject(
    organizationId,
    workspaceId,
  );

  const handlePublishProject = async (formData?: string) => {
    try {
      await mutateAsyncPublishProject(formData);
      toast.success(
        `${lang.get('msg.project')} "${projectTitle}" ${lang.get('msg.isSuccessfullyPublished')}`,
      );
      onClose();
    } catch (error) {
      // These axios instances reject with the unwrapped response, so read via
      // the getError* helpers (never error.response.X) — see getErrorDetails.ts.
      const status = getErrorStatus(error);
      const backendMessage = getErrorMessage(error);
      if (
        status === StatusCodes.FORBIDDEN &&
        backendMessage === EPublishErrorMessage.NOT_SUBSCRIBED
      ) {
        // Only the genuine "not subscribed" 403 should send users to sales.
        toast.error(
          themeName === ECompanyThemeName.EMPATHY
            ? lang.get('msg.projectSubscriptionPublishError_empathy')
            : lang.get('msg.projectSubscriptionPublishError'),
        );
      } else if (backendMessage === EPublishErrorMessage.PROJECT_NOT_READY) {
        // The project failed publish-readiness validation, not a billing issue.
        toast.error(lang.get('msg.someOfRequiredFieldsAreEmpty'));
      } else {
        toast.error(lang.get('msg.projectIsNotPublishedPleaseTryAgain'));
      }
    }
  };

  const isEmpathyTheme = themeName === ECompanyThemeName.EMPATHY;

  return (
    <AlertDialog open={isOpen}>
      {isOpen && (
        <AlertDialogContent className="max-w-lg max-h-[90vh] overflow-y-auto flex flex-col gap-14">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex flex-wrap items-center gap-2">
              {lang.get('msg.publish')} {isEmpathyTheme ? 'project' : 'Tellet'}
            </AlertDialogTitle>
            <p className="text-sm text-dark-text">
              {isEmpathyTheme
                ? lang.get('msg.areYouSureYouWantToPublishThisTellet_empathy')
                : lang.get('msg.areYouSureYouWantToPublishThisTellet')}
              <span className="font-semibold">{` - ${projectTitle}?`}</span>
            </p>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={onClose}>{lang.get('msg.cancel')}</AlertDialogCancel>
            <Button
              variant="default"
              onClick={() => {
                handlePublishProject(projectId);
              }}
              disabled={isPending}
              type="button"
            >
              {isPublishRequestApproved ? lang.get('msg.requestApproved') : lang.get('msg.publish')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      )}
    </AlertDialog>
  );
}

export default PublishProjectModal;
